import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';

Modal.setAppElement("#root");

export default function MediaPreviewModal({
  isOpen,
  onRequestClose,
  mediaItem,
  mediaFiles = [],
  currentIndex = 0,
  onNavigate
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [textContent, setTextContent] = useState('');
  const [currentMedia, setCurrentMedia] = useState(null);

  // Helper function to get MIME type from file name
  const getMimeTypeFromFileName = (fileName) => {
    if (!fileName) return 'application/octet-stream';
   
    const ext = fileName.split('.').pop().toLowerCase();
    const mimeTypes = {
      // Images
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'bmp': 'image/bmp',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
     
      // Videos
      'mp4': 'video/mp4',
      'avi': 'video/x-msvideo',
      'mov': 'video/quicktime',
      'wmv': 'video/x-ms-wmv',
      'flv': 'video/x-flv',
      'webm': 'video/webm',
      'mkv': 'video/x-matroska',
      'm4v': 'video/mp4',
      '3gp': 'video/3gpp',
     
      // Documents
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'txt': 'text/plain'
    };
   
    return mimeTypes[ext] || 'application/octet-stream';
  };

  // Update current media when props change
  useEffect(() => {
    const newCurrentMedia = mediaItem || (mediaFiles.length > 0 ? mediaFiles[currentIndex] : null);
    setCurrentMedia(newCurrentMedia);
    setIsLoading(true);
    setTextContent('');
  }, [mediaItem, mediaFiles, currentIndex]);

  // Load text content for text files
  useEffect(() => {
    if (currentMedia && (currentMedia.type === 'text' || currentMedia.type === 'document')) {
      const fileName = currentMedia.name?.toLowerCase() || '';
      if (fileName.endsWith('.txt')) {
        setIsLoading(true);
        fetch(currentMedia.previewUrl)
          .then(response => response.text())
          .then(text => {
            setTextContent(text);
            setIsLoading(false);
          })
          .catch(err => {
            console.error('Failed to load text file:', err);
            setTextContent('Failed to load text content');
            setIsLoading(false);
          });
      } else {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [currentMedia]);

  const renderMediaContent = () => {
    if (!currentMedia) {
      return (
        <div className="media-preview-placeholder">
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
          <p>No media to preview.</p>
        </div>
      );
    }

    const { previewUrl, type, name } = currentMedia;
    
    // Determine media type based on file extension and type
    const fileName = name?.toLowerCase() || '';
    const isImage = type === 'image' || /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(fileName);
    const isPDF = type === 'pdf' || fileName.endsWith('.pdf');
    const isVideo = type === 'video' || /\.(mp4|avi|mov|wmv|flv|webm|mkv)$/i.test(fileName);
    const isText = type === 'text' || type === 'document' || fileName.endsWith('.txt');

    if (isImage) {
      return (
        <div className="media-content-wrapper">
          {isLoading && (
            <div className="media-loading">
              <div className="loading-spinner"></div>
              <p>Loading image...</p>
            </div>
          )}
          <img 
            src={previewUrl} 
            alt="Preview" 
            className="media-preview-image-fullscreen"
            style={{ 
              display: isLoading ? 'none' : 'block'
            }}
            onLoad={() => setIsLoading(false)}
            onError={(e) => {
              console.error('Failed to load image:', previewUrl);
              e.target.alt = 'Failed to load image';
              setIsLoading(false);
            }}
          />
        </div>
      );
    }

    if (isPDF) {
      return (
        <div className="media-content-wrapper">
          {isLoading && (
            <div className="media-loading">
              <div className="loading-spinner"></div>
              <p>Loading PDF...</p>
            </div>
          )}
          <iframe
            src={previewUrl}
            className="media-preview-document-fullscreen"
            style={{ 
              display: isLoading ? 'none' : 'block'
            }}
            title="PDF Preview"
            onLoad={() => setIsLoading(false)}
            onError={() => setIsLoading(false)}
          />
        </div>
      );
    }

    if (isVideo) {
      return (
        <div className="media-content-wrapper">
          <video
            controls
            autoPlay
            className="media-preview-video-fullscreen"
            onLoadStart={() => setIsLoading(true)}
            onCanPlay={() => setIsLoading(false)}
            onError={() => setIsLoading(false)}
          >
            <source src={previewUrl} type={getMimeTypeFromFileName(name)} />
            Your browser does not support the video tag.
          </video>
          {isLoading && (
            <div className="media-loading">
              <div className="loading-spinner"></div>
              <p>Loading video...</p>
            </div>
          )}
        </div>
      );
    }

    if (isText) {
      return (
        <div className="media-content-wrapper">
          {isLoading && (
            <div className="media-loading">
              <div className="loading-spinner"></div>
              <p>Loading text content...</p>
            </div>
          )}
          <div
            className="media-preview-text-fullscreen"
            style={{ 
              display: isLoading ? 'none' : 'block',
              width: '100%',
              height: '100%',
              padding: '20px',
              backgroundColor: 'white',
              color: 'black',
              fontFamily: 'monospace',
              fontSize: '14px',
              lineHeight: '1.5',
              whiteSpace: 'pre-wrap',
              overflow: 'auto'
            }}
          >
            {textContent || 'No text content available'}
          </div>
        </div>
      );
    }

    // Fallback for unsupported file types
    return (
      <div className="media-preview-placeholder">
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
        <p>Preview not available for this file type.</p>
        <p>File type: {type}</p>
        <p>File name: {name}</p>
        <a 
          href={previewUrl} 
          download={name}
          style={{
            padding: '10px 20px',
            background: '#007bff',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '4px',
            marginTop: '20px',
            display: 'inline-block'
          }}
        >
          Download File
        </a>
      </div>
    );
  };

  const hasMultipleFiles = mediaFiles.length > 1;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="media-preview-modal-fullscreen"
      overlayClassName="media-preview-overlay-fullscreen"
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
    >
      {/* Header with controls */}
      <div className="media-preview-header-fullscreen">
        <div className="media-preview-title-fullscreen">
          {currentMedia?.name || 'Media Preview'}
        </div>
        <div className="media-preview-controls-fullscreen">
          {hasMultipleFiles && onNavigate && (
            <span className="media-counter-fullscreen">
              {currentIndex + 1} / {mediaFiles.length}
            </span>
          )}
          <button 
            className="media-preview-control-btn-fullscreen"
            onClick={onRequestClose}
            aria-label="Close preview"
            title="Close preview"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Main preview content */}
      <div className="media-preview-content-fullscreen">
        {renderMediaContent()}
      </div>

      {/* Navigation Arrows for Multiple Files */}
      {hasMultipleFiles && onNavigate && (
        <>
          <button 
            className="media-preview-nav-fullscreen media-preview-prev-fullscreen"
            onClick={() => onNavigate(-1)}
            aria-label="Previous media"
          >
            ‹
          </button>
          <button 
            className="media-preview-nav-fullscreen media-preview-next-fullscreen"
            onClick={() => onNavigate(1)}
            aria-label="Next media"
          >
            ›
          </button>
        </>
      )}
      <style jsx>{`
        /* Fullscreen Media Preview Modal Styles */
        .media-preview-overlay-fullscreen {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.95);
          z-index: 1200;
          animation: fadeIn 0.2s ease;
        }

        .media-preview-modal-fullscreen {
          position: relative;
          background: #000;
          border: none;
          border-radius: 0;
          padding: 0;
          width: 100vw;
          height: 100vh;
          margin: 0;
          display: flex;
          flex-direction: column;
          animation: fadeIn 0.2s ease;
          outline: none;
        }

        .media-preview-header-fullscreen {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          background: rgba(0, 0, 0, 0.8);
          border-bottom: 1px solid #333;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          z-index: 10;
          backdrop-filter: blur(10px);
        }

        .media-preview-title-fullscreen {
          color: #fff;
          font-weight: 500;
          font-size: 18px;
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .media-preview-controls-fullscreen {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .media-counter-fullscreen {
          color: #ccc;
          font-size: 16px;
          margin-right: 12px;
        }

        .media-preview-control-btn-fullscreen {
          background: rgba(255, 255, 255, 0.15);
          border: none;
          color: white;
          width: 40px;
          height: 40px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          transition: all 0.2s ease;
        }

        .media-preview-control-btn-fullscreen:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: scale(1.05);
        }

        .media-preview-content-fullscreen {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100vh;
          position: relative;
          overflow: hidden;
          padding-top: 73px; /* Account for header height */
          box-sizing: border-box;
        }

        .media-content-wrapper {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .media-loading {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
          color: #ccc;
          z-index: 5;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: #fff;
          animation: spin 1s ease-in-out infinite;
          margin: 0 auto 12px;
        }

        .media-preview-nav-fullscreen {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.2);
          color: white;
          border: none;
          border-radius: 50%;
          width: 60px;
          height: 60px;
          font-size: 28px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          z-index: 10;
          backdrop-filter: blur(10px);
        }

        .media-preview-nav-fullscreen:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-50%) scale(1.1);
        }

        .media-preview-prev-fullscreen {
          left: 30px;
        }

        .media-preview-next-fullscreen {
          right: 30px;
        }

        /* Fullscreen Media Content Styles */
        .media-preview-image-fullscreen {
          max-width: 100%;
          max-height: 100%;
          width: auto;
          height: auto;
          object-fit: contain;
        }

        .media-preview-document-fullscreen {
          width: 100%;
          height: 100%;
          border: none;
          background: white;
        }

        .media-preview-video-fullscreen {
          width: 100%;
          height: 100%;
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          background: #000;
        }

        .media-preview-text-fullscreen {
          width: 100%;
          height: 100%;
          border: none;
          background: white;
          font-family: 'Courier New', monospace;
          font-size: 14px;
          line-height: 1.5;
          white-space: pre-wrap;
          overflow: auto;
          padding: 20px;
        }

        .media-preview-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #333;
          color: white;
          border-radius: 4px;
          padding: 40px;
        }

        /* Animations */
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .media-preview-nav-fullscreen {
            width: 50px;
            height: 50px;
            font-size: 24px;
          }

          .media-preview-prev-fullscreen {
            left: 15px;
          }

          .media-preview-next-fullscreen {
            right: 15px;
          }

          .media-preview-header-fullscreen {
            padding: 12px 16px;
          }

          .media-preview-title-fullscreen {
            font-size: 16px;
          }

          .media-preview-control-btn-fullscreen {
            width: 35px;
            height: 35px;
            font-size: 16px;
          }
        }

        @media (max-width: 480px) {
          .media-preview-nav-fullscreen {
            width: 40px;
            height: 40px;
            font-size: 20px;
          }

          .media-preview-header-fullscreen {
            padding: 10px 12px;
          }

          .media-preview-title-fullscreen {
            font-size: 14px;
          }
        }
      `}</style>
    </Modal>
  );
}